import { useState, useRef, useCallback, useEffect } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link2, Palette, Eraser, ChevronDown, ChevronUp } from 'lucide-react';

const COLLAPSED_HEIGHT = 140;
const EXPANDED_HEIGHT = 360;

// Curated palette (mirrors the common Quill default set, trimmed for a compact popover)
const COLORS = [
  '#000000', '#5c5c5c', '#8a8a8a', '#bbbbbb', '#ffffff',
  '#e60000', '#ff9900', '#ffcc00', '#008a00', '#0066cc',
  '#9933ff', '#a10000', '#b26b00', '#006100', '#0047b2',
];

// Content is truly empty only if there's no text AND no meaningful formatting
function isTrulyEmpty(html, plainText) {
  if (!html) return true;
  if ((plainText || '').trim().length > 0) return false;
  // Has list formatting, links, or images: not empty
  if (/<(ol|ul|li|a|img)\b/i.test(html)) return false;
  return true;
}

// Normalize an empty-ish contenteditable value to '' so callers store a clean empty state
function readValue(el) {
  if (!el) return '';
  const html = el.innerHTML;
  const text = el.textContent || '';
  return isTrulyEmpty(html, text) ? '' : html;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Write something...',
  collapsedHeight = COLLAPSED_HEIGHT,
  expandedHeight = EXPANDED_HEIGHT,
  className = '',
  id,
  ariaLabel,
  'data-testid': testId,
}) {
  const [expanded, setExpanded] = useState(false);
  const [isEmpty, setIsEmpty] = useState(isTrulyEmpty(value, ''));
  const [colorOpen, setColorOpen] = useState(false);
  const [active, setActive] = useState({ bold: false, italic: false, underline: false });

  const editorRef = useRef(null);
  const lastEmittedRef = useRef(null);

  // Keep the contenteditable DOM in sync with the controlled `value` prop.
  // We compare against the last value WE emitted so that regular typing
  // (where the parent just echoes our value back) never resets the caret.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const incoming = value || '';
    if (incoming === lastEmittedRef.current) return;
    el.innerHTML = incoming;
    lastEmittedRef.current = incoming;
    setIsEmpty(isTrulyEmpty(incoming, el.textContent || ''));
  }, [value]);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = readValue(el);
    lastEmittedRef.current = next;
    setIsEmpty(next === '');
    onChange?.(next);
  }, [onChange]);

  const refreshActive = useCallback(() => {
    try {
      setActive({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
      });
    } catch {
      /* queryCommandState can throw when no selection; ignore */
    }
  }, []);

  // Track active formatting for the current selection while the editor is focused
  useEffect(() => {
    const handler = () => {
      const el = editorRef.current;
      const sel = window.getSelection();
      if (!el || !sel || sel.rangeCount === 0) return;
      if (el.contains(sel.anchorNode)) refreshActive();
    };
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
  }, [refreshActive]);

  const exec = useCallback((command, arg) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    try {
      document.execCommand(command, false, arg);
    } catch {
      /* execCommand is universally supported; guard defensively */
    }
    emitChange();
    refreshActive();
  }, [emitChange, refreshActive]);

  const insertLink = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    const hasSelection = sel && sel.toString().length > 0;
    const url = window.prompt('Enter the link URL');
    if (!url) return;
    if (hasSelection) {
      exec('createLink', url);
    } else {
      // No selection: insert the URL as its own linked text
      exec('insertHTML', `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);
    }
  }, [exec]);

  const applyColor = useCallback((color) => {
    setColorOpen(false);
    exec('foreColor', color);
  }, [exec]);

  const height = expanded ? expandedHeight : collapsedHeight;

  // Prevent the toolbar from stealing focus (which would drop the selection)
  const keepFocus = (e) => e.preventDefault();

  const ToolBtn = ({ icon: Icon, title, onClick, isActive, testid }) => (
    <button
      type="button"
      className={`rte-btn ${isActive ? 'rte-btn-active' : ''}`}
      title={title}
      aria-label={title}
      onMouseDown={keepFocus}
      onClick={onClick}
      data-testid={testid}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <div
      className={`rte-wrapper ${className}`}
      data-testid={testId || 'rich-text-editor'}
      id={id}
    >
      <div className="rte-toolbar" data-testid={testId ? `${testId}-toolbar` : 'rte-toolbar'}>
        <ToolBtn icon={Bold} title="Bold" isActive={active.bold} onClick={() => exec('bold')} testid="rte-bold" />
        <ToolBtn icon={Italic} title="Italic" isActive={active.italic} onClick={() => exec('italic')} testid="rte-italic" />
        <ToolBtn icon={Underline} title="Underline" isActive={active.underline} onClick={() => exec('underline')} testid="rte-underline" />
        <span className="rte-sep" />
        <ToolBtn icon={ListOrdered} title="Numbered list" onClick={() => exec('insertOrderedList')} testid="rte-ol" />
        <ToolBtn icon={List} title="Bullet list" onClick={() => exec('insertUnorderedList')} testid="rte-ul" />
        <span className="rte-sep" />
        <ToolBtn icon={Link2} title="Insert link" onClick={insertLink} testid="rte-link" />
        <div className="rte-color-wrap">
          <button
            type="button"
            className="rte-btn"
            title="Text color"
            aria-label="Text color"
            onMouseDown={keepFocus}
            onClick={() => setColorOpen((o) => !o)}
            data-testid="rte-color"
          >
            <Palette className="h-3.5 w-3.5" />
          </button>
          {colorOpen && (
            <div className="rte-color-pop" data-testid="rte-color-popover">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="rte-swatch"
                  style={{ background: c }}
                  title={c}
                  onMouseDown={keepFocus}
                  onClick={() => applyColor(c)}
                  data-testid={`rte-swatch-${c.replace('#', '')}`}
                />
              ))}
            </div>
          )}
        </div>
        <span className="rte-sep" />
        <ToolBtn icon={Eraser} title="Remove styling" onClick={() => exec('removeFormat')} testid="rte-clean" />
      </div>

      <div className="rte-editor-area" style={{ height }}>
        <div
          ref={editorRef}
          className="rte-content"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={ariaLabel || placeholder || 'Rich text editor'}
          data-testid={testId ? `${testId}-input` : 'rte-input'}
          onInput={emitChange}
          onBlur={emitChange}
          onFocus={refreshActive}
        />
        {isEmpty && <div className="rte-placeholder">{placeholder}</div>}
      </div>

      <button
        type="button"
        className="rte-expand-toggle"
        onClick={() => setExpanded((e) => !e)}
        data-testid={testId ? `${testId}-expand` : 'rte-expand-toggle'}
      >
        {expanded ? (
          <><ChevronUp className="h-3 w-3" /> Less</>
        ) : (
          <><ChevronDown className="h-3 w-3" /> More</>
        )}
      </button>

      <style>{`
        .rte-wrapper {
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          background: hsl(var(--background));
          position: relative;
        }
        /* Toolbar */
        .rte-wrapper .rte-toolbar {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 1px;
          border-bottom: 1px solid hsl(var(--border));
          background: hsl(var(--secondary));
          padding: 4px 6px;
          border-radius: 6px 6px 0 0;
        }
        .rte-wrapper .rte-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 24px;
          width: 24px;
          border: none;
          background: transparent;
          color: hsl(var(--muted-foreground));
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.12s ease, color 0.12s ease;
        }
        .rte-wrapper .rte-btn:hover {
          background: hsl(var(--background));
          color: hsl(var(--primary));
        }
        .rte-wrapper .rte-btn-active {
          color: hsl(var(--primary));
          background: hsl(var(--background));
        }
        .rte-wrapper .rte-sep {
          width: 1px;
          height: 16px;
          background: hsl(var(--border));
          margin: 0 3px;
        }
        /* Color popover */
        .rte-wrapper .rte-color-wrap {
          position: relative;
          display: inline-flex;
        }
        .rte-wrapper .rte-color-pop {
          position: absolute;
          top: 28px;
          left: 0;
          z-index: 50;
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 4px;
          padding: 6px;
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }
        .rte-wrapper .rte-swatch {
          width: 16px;
          height: 16px;
          border-radius: 3px;
          border: 1px solid hsl(var(--border));
          cursor: pointer;
          padding: 0;
        }
        .rte-wrapper .rte-swatch:hover {
          transform: scale(1.12);
        }
        /* Editor area */
        .rte-editor-area {
          transition: height 0.2s ease;
          overflow: hidden;
          position: relative;
        }
        .rte-wrapper .rte-content {
          height: 100%;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 8px 10px;
          font-size: 13px;
          line-height: 1.5;
          color: hsl(var(--foreground));
          outline: none;
        }
        .rte-wrapper .rte-content:focus {
          outline: none;
        }
        .rte-wrapper .rte-content a {
          color: hsl(var(--primary));
          text-decoration: underline;
        }
        .rte-wrapper .rte-content ul,
        .rte-wrapper .rte-content ol {
          padding-left: 22px;
          margin: 4px 0;
        }
        .rte-wrapper .rte-content li {
          margin: 2px 0;
        }
        .rte-wrapper .rte-placeholder {
          position: absolute;
          top: 8px;
          left: 10px;
          font-size: 13px;
          color: hsl(var(--muted-foreground));
          pointer-events: none;
          user-select: none;
        }
        /* Expand toggle */
        .rte-wrapper .rte-expand-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          width: 100%;
          padding: 3px 0;
          border: none;
          border-top: 1px solid hsl(var(--border));
          background: hsl(var(--secondary) / 0.5);
          color: hsl(var(--muted-foreground));
          font-size: 11px;
          cursor: pointer;
          transition: background-color 0.15s;
          border-radius: 0 0 6px 6px;
        }
        .rte-wrapper .rte-expand-toggle:hover {
          background: hsl(var(--secondary));
          color: hsl(var(--foreground));
        }
      `}</style>
    </div>
  );
}
